/* ==========================================================================
   Auth API Functions
   Authentication endpoints for provider login/logout
   ========================================================================== */

import apiClient, {
  getStoredToken,
  setStoredToken,
  clearStoredToken,
  ApiClientError,
} from './client'
import { keyFor, getCurrentSlug, withPortalSlug } from '@/utils/portalSlug'
import { safeGetItem, safeSetItem, safeRemoveItem } from '@/utils/safeStorage'
import {
  getTwoFactorDeviceToken,
  setTwoFactorDeviceToken,
  clearTwoFactorDeviceToken,
} from '@/utils/twoFactorDevice'
import { isPortalSelection, isTwoFactorChallenge } from './types'
import type {
  LoginRequest,
  LoginResponse,
  PortalSelectionResponse,
  TokenResponse,
  TwoFactorChallengeResponse,
  TwoFactorLoginRequest,
  TwoFactorStatusResponse,
  TwoFactorSetupResponse,
  SetPasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  MessageResponse,
  ProfileResponse,
  ProfileUpdate,
  ProviderAddressCreate,
  ProviderAddressListResponse,
  ProviderAddressResponse,
  ProviderAddressUpdate,
} from './types'

/* --------------------------------------------------------------------------
   Storage Keys
   -------------------------------------------------------------------------- */

const TOKEN_EXPIRY_KEY = 'tjm_token_expires_at'
const REMEMBER_ME_KEY = 'tjm_remember_me'

/* --------------------------------------------------------------------------
   Token Expiry Management

   Like the access token, expiry and remember-me are stored per portal slug so
   each portal session persists/expires independently. Helpers default to the
   slug in the current URL.
   -------------------------------------------------------------------------- */

export function getStoredTokenExpiry(slug?: string | null): Date | null {
  const expiry = safeGetItem(keyFor(TOKEN_EXPIRY_KEY, slug))
  if (!expiry) return null
  return new Date(expiry)
}

export function setStoredTokenExpiry(expiresAt: string, slug?: string | null): void {
  safeSetItem(keyFor(TOKEN_EXPIRY_KEY, slug), expiresAt)
}

export function clearStoredTokenExpiry(slug?: string | null): void {
  safeRemoveItem(keyFor(TOKEN_EXPIRY_KEY, slug))
}

export function getRememberMe(slug?: string | null): boolean {
  return safeGetItem(keyFor(REMEMBER_ME_KEY, slug)) === 'true'
}

export function setRememberMe(remember: boolean, slug?: string | null): void {
  if (remember) {
    safeSetItem(keyFor(REMEMBER_ME_KEY, slug), 'true')
  } else {
    safeRemoveItem(keyFor(REMEMBER_ME_KEY, slug))
  }
}

export function clearRememberMe(slug?: string | null): void {
  safeRemoveItem(keyFor(REMEMBER_ME_KEY, slug))
}

/**
 * Check if the stored token is expired (defaults to the current portal slug).
 */
export function isTokenExpired(slug?: string | null): boolean {
  const expiry = getStoredTokenExpiry(slug)
  if (!expiry) return true
  // Add 30 second buffer to account for network latency
  return new Date() >= new Date(expiry.getTime() - 30000)
}

/* --------------------------------------------------------------------------
   Auth API Functions
   -------------------------------------------------------------------------- */

function storeSession(
  data: TokenResponse,
  slug: string | null,
  rememberMe: boolean
): void {
  setStoredToken(data.access_token, slug)
  setStoredTokenExpiry(data.expires_at, slug)
  setRememberMe(rememberMe, slug)
}

/**
 * Login with email and password.
 * Stores the token and expiry in localStorage. When the account has 2FA on
 * (PORTAL-187) nothing is stored and the challenge is returned instead; finish
 * with `loginTwoFactor`. Without a portal slug, credentials valid at several
 * pharmacies return a portal selection (PORTAL-199); nothing is stored then either.
 */
export async function login(
  request: LoginRequest
): Promise<LoginResponse | TwoFactorChallengeResponse | PortalSelectionResponse> {
  // Scope the lookup to the portal in the URL so an email registered in
  // multiple pharmacies resolves to the right account.
  const scoped = withPortalSlug(request)
  const body = {
    ...scoped,
    two_factor_device_token:
      request.two_factor_device_token ??
      getTwoFactorDeviceToken('provider', request.email, scoped.portal_slug),
  }
  const response = await apiClient.post<
    LoginResponse | TwoFactorChallengeResponse | PortalSelectionResponse
  >('/auth/login', body)
  if (isTwoFactorChallenge(response.data) || isPortalSelection(response.data)) {
    return response.data
  }

  // Store under the slug the backend authenticated against, so the request
  // interceptor (which reads the URL slug) finds the token once the portal loads.
  // The response's slug covers logins from the general page, which send none.
  storeSession(
    response.data,
    response.data.portal_slug || body.portal_slug || null,
    request.remember_me ?? false
  )

  return response.data
}

/**
 * Second login step: the challenge pass plus the authenticator code (PORTAL-187).
 * Stores the session like `login`, and the "remember this device" token if issued.
 */
export async function loginTwoFactor(
  request: TwoFactorLoginRequest,
  email: string,
  rememberMe: boolean
): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/auth/login/two-factor', request)
  const slug = response.data.portal_slug || getCurrentSlug()
  storeSession(response.data, slug, rememberMe)
  setTwoFactorDeviceToken('provider', email, response.data.two_factor_device_token, slug)
  return response.data
}

/**
 * Logout the current user.
 * Clears all stored auth data.
 * Task F32: Sends Authorization header for server-side token revocation.
 */
export async function logout(): Promise<void> {
  // Capture token before clearing - needed for server-side revocation
  const token = getStoredToken()

  try {
    // Only call the API if we have a token to revoke
    if (token) {
      await apiClient.post('/auth/logout', null, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    }
  } catch (error) {
    // Ignore errors on logout - we'll clear local data anyway
    // This ensures logout completes even if the server is unreachable
    if (!(error instanceof ApiClientError && error.status === 401)) {
      console.warn('Logout API call failed:', error)
    }
  } finally {
    // Always clear local auth data regardless of API response
    clearStoredToken()
    clearStoredTokenExpiry()
    clearRememberMe()
  }
}

/**
 * Accept invitation and set initial password.
 * Returns a token for auto-login.
 */
export async function acceptInvitation(
  request: SetPasswordRequest
): Promise<TokenResponse> {
  const response = await apiClient.post<TokenResponse>(
    '/auth/set-password',
    request
  )
  return response.data
}

/**
 * Request a password reset email.
 */
export async function forgotPassword(
  request: ForgotPasswordRequest
): Promise<MessageResponse> {
  const body = withPortalSlug(request)
  const response = await apiClient.post<MessageResponse>(
    '/auth/forgot-password',
    body
  )
  return response.data
}

/**
 * Reset password with token from email.
 */
export async function resetPassword(
  request: ResetPasswordRequest
): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>(
    '/auth/reset-password',
    request
  )
  return response.data
}

/**
 * Change password for the currently authenticated provider.
 */
export async function changePassword(
  request: ChangePasswordRequest
): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>(
    '/auth/change-password',
    request
  )
  return response.data
}

/* --------------------------------------------------------------------------
   Two-factor settings (PORTAL-187)
   -------------------------------------------------------------------------- */

export async function getTwoFactorStatus(): Promise<TwoFactorStatusResponse> {
  const response = await apiClient.get<TwoFactorStatusResponse>('/auth/two-factor')
  return response.data
}

export async function startTwoFactorSetup(): Promise<TwoFactorSetupResponse> {
  const response = await apiClient.post<TwoFactorSetupResponse>('/auth/two-factor/setup')
  return response.data
}

/** Turn 2FA on. This browser is not remembered: that is opt-in at the login step. */
export async function enableTwoFactor(code: string): Promise<TwoFactorStatusResponse> {
  const response = await apiClient.post<TwoFactorStatusResponse>(
    '/auth/two-factor/enable',
    { code }
  )
  return response.data
}

export async function disableTwoFactor(
  code: string,
  email: string
): Promise<TwoFactorStatusResponse> {
  const response = await apiClient.post<TwoFactorStatusResponse>(
    '/auth/two-factor/disable',
    { code }
  )
  clearTwoFactorDeviceToken('provider', email, getCurrentSlug())
  return response.data
}

/**
 * Get the current user's profile.
 */
export async function getProfile(): Promise<ProfileResponse> {
  const response = await apiClient.get<ProfileResponse>('/providers/me')
  return response.data
}

/**
 * Update the current user's profile.
 */
export async function updateProfile(
  data: ProfileUpdate
): Promise<ProfileResponse> {
  const response = await apiClient.put<ProfileResponse>('/providers/me', data)
  return response.data
}

/* --------------------------------------------------------------------------
   Provider practice address endpoints (issues #140 / #180)
   -------------------------------------------------------------------------- */

/**
 * List the authenticated provider's practice addresses.
 */
export async function listMyAddresses(): Promise<ProviderAddressListResponse> {
  const response = await apiClient.get<ProviderAddressListResponse>(
    '/providers/me/addresses'
  )
  return response.data
}

/**
 * Add a new practice address for the authenticated provider.
 */
export async function createMyAddress(
  data: ProviderAddressCreate
): Promise<ProviderAddressResponse> {
  const response = await apiClient.post<ProviderAddressResponse>(
    '/providers/me/addresses',
    data
  )
  return response.data
}

/**
 * Update an existing practice address.
 */
export async function updateMyAddress(
  addressId: string,
  data: ProviderAddressUpdate
): Promise<ProviderAddressResponse> {
  const response = await apiClient.put<ProviderAddressResponse>(
    `/providers/me/addresses/${addressId}`,
    data
  )
  return response.data
}

/**
 * Delete an existing practice address.
 */
export async function deleteMyAddress(addressId: string): Promise<void> {
  await apiClient.delete(`/providers/me/addresses/${addressId}`)
}

/* --------------------------------------------------------------------------
   Exports
   -------------------------------------------------------------------------- */

export default {
  login,
  logout,
  acceptInvitation,
  forgotPassword,
  resetPassword,
  changePassword,
  getProfile,
  updateProfile,
  listMyAddresses,
  createMyAddress,
  updateMyAddress,
  deleteMyAddress,
}
